import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ShieldAlert } from "lucide-react";
import { EASE } from "../components/Reveal";

const TERMS = [
    {
        h: "1. Acceptance of Terms",
        p: "By downloading, accessing, or using this healthcare application, you agree to be bound by these Terms and Conditions. If you do not agree, please discontinue use immediately.",
    },
    { h: "2. Medical Disclaimer", warning: true },
    {
        h: "3. Appointment Booking & Cancellations",
        p: "Users may book appointments with available healthcare providers via the platform. Confirmations, reschedules, and cancellations are subject to the specific provider's operational policies and any associated platform transaction fees.",
    },
    {
        h: "4. Account Responsibility",
        p: "You are solely responsible for safeguarding the credentials used to access your account and for any activities or actions performed under your password.",
    },
];

const PRIVACY = [
    {
        h: "1. Data Collection",
        p: "We collect personal identifiers (name, date of birth, phone number) to handle your appointment scheduling, alongside uploadable personal health documents, vitals, and laboratory reports for processing data analysis requests.",
    },
    {
        h: "2. Health Record Analysis",
        p: "Your health records are processed utilizing secure algorithms to offer organized summaries and health trends. We enforce rigorous end-to-end data encryption architectures during both transmission and storage states.",
    },
    {
        h: "3. Sharing of Information",
        p: "Health metrics and personal details are exclusively shared with healthcare providers explicitly designated by you during appointment bookings. We do not sell or leverage health telemetry data for marketing frameworks.",
    },
    {
        h: "4. Regulatory Compliance",
        p: "We maintain systemic administrative protocols aligned with standard healthcare data compliance regulations (such as HIPAA rules or regional digital health privacy mandates) to secure user information fields against unapproved exposures.",
    },
];

const WARNING_TEXT =
    "CRITICAL: No user medical data is being stored directly without encryption and the application is compliant with digital privacy laws. The application involves both doctors / health supporters and artificial intelligence so usage is completely dependent on the users.";

export default function Legal() {
    const [tab, setTab] = useState("terms");
    const sections = tab === "terms" ? TERMS : PRIVACY;

    return (
        <motion.main
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="bg-[#17151B] text-[#F7F3FA] min-h-screen"
            data-testid="legal-page"
        >
            <header className="border-b hairline sticky top-0 z-40 bg-[#17151B]/80 backdrop-blur-xl">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <span className="flex items-baseline gap-1">
                        <span className="font-serif-display text-2xl font-semibold tracking-tight">Femipal</span>
                        <span className="w-1.5 h-1.5 rounded-full bg-[#F3698F]" />
                    </span>
                    <Link
                        to="/"
                        data-testid="legal-home-link"
                        className="overline-label border hairline px-5 py-2 rounded-full text-[#D1C6DF] hover:text-white hover:border-[#F3698F]/60 transition-colors duration-300"
                    >
                        Visit Femipal
                    </Link>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 py-16 md:py-24">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
                    className="mb-12"
                >
                    <p className="overline-label text-[#F3698F] mb-4">Femipal — Legal</p>
                    <h1 className="font-serif-display font-light tracking-tight text-4xl md:text-6xl" data-testid="legal-title">
                        Legal Documents
                    </h1>
                    <p className="text-[#D1C6DF] mt-4 text-base md:text-lg">
                        Please review our app terms and privacy protocols.
                    </p>
                </motion.div>

                <div className="flex gap-8 border-b hairline mb-10" data-testid="legal-tabs">
                    {[
                        { id: "terms", label: "Terms of Service" },
                        { id: "privacy", label: "Privacy Policy" },
                    ].map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`legal-tab-${t.id}`}
                            className={`relative pb-4 text-sm md:text-base font-medium tracking-wide transition-colors duration-300 ${tab === t.id ? "text-white" : "text-[#D1C6DF] hover:text-white"
                                }`}
                        >
                            {t.label}
                            {tab === t.id && (
                                <motion.span
                                    layoutId="legal-tab-underline"
                                    className="absolute left-0 right-0 -bottom-px h-0.5 bg-[#F3698F]"
                                    transition={{ duration: 0.4, ease: EASE }}
                                />
                            )}
                        </button>
                    ))}
                </div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={tab}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.45, ease: EASE }}
                        data-testid={`legal-content-${tab}`}
                    >
                        {sections.map((s) =>
                            s.warning ? (
                                <div key={s.h} className="mb-10">
                                    <h3 className="font-serif-display text-2xl font-medium mb-4">{s.h}</h3>
                                    <div
                                        className="border border-[#F3698F]/30 bg-[#F3698F]/5 text-[#F3698F] rounded-sm p-6 text-sm md:text-base leading-relaxed flex gap-4"
                                        data-testid="legal-warning-box"
                                    >
                                        <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" strokeWidth={1.5} />
                                        <p>{WARNING_TEXT}</p>
                                    </div>
                                </div>
                            ) : (
                                <div key={s.h} className="mb-10">
                                    <h3 className="font-serif-display text-2xl font-medium mb-3">{s.h}</h3>
                                    <p className="text-[#D1C6DF] leading-relaxed">{s.p}</p>
                                </div>
                            )
                        )}
                        <p className="overline-label text-[#8F82A2] pt-6 border-t hairline">Last updated — July 2026</p>
                    </motion.div>
                </AnimatePresence>
            </div>
        </motion.main>
    );
}